import { createTheme } from '@material-ui/core';

import { palette } from './palette';
import { typography } from './typography';
import { tokens, fonts, withAlpha } from './tokens';

let RADIUS = 12;
let RADIUS_SM = 10;

// Tema claro de la consola. Casi toda la transformación visual vive en estos
// overrides: react-admin y MUI v4 traen superficies por defecto poco marca, así
// que se redefinen tablas, tarjetas, inputs, botones, menús, tooltips, paginación
// y notificaciones para la paleta Marfil/Noche/Ultramarino (fondo claro).
export let theme = createTheme({
  palette,
  typography,
  shape: { borderRadius: RADIUS },
  // Anchos del sidebar que usa el Layout de react-admin.
  sidebar: { width: 248, closedWidth: 64 },
  props: {
    MuiButtonBase: { disableRipple: false },
    MuiPaper: { elevation: 0 },
    MuiCard: { elevation: 0 },
    MuiAppBar: { elevation: 0 },
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        html: { WebkitFontSmoothing: 'antialiased' },
        body: {
          margin: 0,
          padding: 0,
          backgroundColor: tokens.bg,
          color: tokens.text,
          fontFamily: fonts.sans,
        },
        '#root': { backgroundColor: tokens.bg, minHeight: '100vh' },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-track': { background: 'transparent' },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: withAlpha(tokens.text, 0.12),
          borderRadius: 8,
          border: `2px solid ${tokens.bg}`,
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: withAlpha(tokens.text, 0.2),
        },
        'input:-webkit-autofill, input:-webkit-autofill:focus': {
          WebkitTextFillColor: tokens.text,
          WebkitBoxShadow: `0 0 0 1000px ${tokens.card2} inset`,
          caretColor: tokens.text,
          transition: 'background-color 9999s ease-out',
        },
        '.yb-mono': { fontFamily: fonts.mono },
      },
    },

    MuiAppBar: {
      root: {
        backgroundColor: tokens.card,
        color: tokens.text,
        borderBottom: `1px solid ${tokens.line}`,
        backgroundImage: 'none',
      },
      colorSecondary: { backgroundColor: tokens.card, color: tokens.text },
    },
    MuiDrawer: {
      paper: {
        backgroundColor: tokens.surface,
        borderRight: `1px solid ${tokens.line}`,
        color: tokens.text,
      },
    },
    MuiToolbar: { dense: { minHeight: 60 } },

    MuiPaper: {
      root: { backgroundColor: tokens.card, color: tokens.text },
      rounded: { borderRadius: RADIUS },
      outlined: { border: `1px solid ${tokens.line}` },
    },
    MuiCard: {
      root: {
        backgroundColor: tokens.card,
        border: `1px solid ${tokens.line}`,
        borderRadius: RADIUS,
        backgroundImage: 'none',
      },
    },
    MuiDivider: { root: { backgroundColor: tokens.line } },

    MuiTable: { root: { backgroundColor: 'transparent' } },
    MuiTableCell: {
      root: {
        borderBottom: `1px solid ${tokens.line}`,
        color: tokens.text,
        fontSize: 13.5,
      },
      sizeSmall: { textAlign: 'left', height: 52, padding: '8px 14px' },
      head: {
        color: tokens.faint,
        fontWeight: 600,
        fontSize: 11,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        borderBottom: `1px solid ${tokens.lineStrong}`,
      },
    },
    MuiTableRow: {
      root: { '&:last-child td': { borderBottom: 'none' } },
      hover: { '&:hover': { backgroundColor: `${tokens.card2} !important` } },
    },
    MuiTableSortLabel: {
      root: {
        color: `${tokens.faint} !important`,
        '&:hover': { color: `${tokens.textDim} !important` },
      },
      active: { color: `${tokens.textDim} !important` },
      icon: { color: 'inherit !important' },
    },

    MuiInputBase: {
      root: { color: tokens.text },
      input: {
        '&::placeholder': { color: tokens.faint, opacity: 1 },
        '&.Mui-disabled': { cursor: 'not-allowed', color: tokens.faint },
      },
    },
    MuiOutlinedInput: {
      root: {
        backgroundColor: tokens.card2,
        borderRadius: RADIUS_SM,
        '& $notchedOutline': { borderColor: tokens.line },
        '&:hover $notchedOutline': { borderColor: tokens.lineStrong },
        '&.Mui-focused $notchedOutline': {
          borderColor: tokens.accent,
          borderWidth: 1,
        },
        '&.Mui-disabled': { backgroundColor: withAlpha(tokens.card2, 0.4) },
        '&.Mui-disabled $notchedOutline': { borderColor: tokens.line },
      },
      input: { color: tokens.text },
      notchedOutline: { borderColor: tokens.line },
    },
    MuiFilledInput: {
      root: {
        backgroundColor: tokens.card2,
        borderRadius: RADIUS_SM,
        '&:hover': { backgroundColor: tokens.card3 },
        '&.Mui-focused': { backgroundColor: tokens.card2 },
        '&.Mui-disabled': { backgroundColor: withAlpha(tokens.card2, 0.4) },
      },
      underline: {
        '&:before': { borderBottomColor: tokens.line },
        '&:after': { borderBottomColor: tokens.accent },
        '&:hover:before': { borderBottomColor: `${tokens.lineStrong} !important` },
      },
    },
    MuiInputLabel: {
      root: { color: tokens.textDim, '&.Mui-focused': { color: tokens.accent } },
    },
    MuiFormLabel: {
      root: {
        color: tokens.textDim,
        '&.Mui-focused': { color: tokens.accent },
        '&.Mui-disabled': { color: tokens.faint },
      },
    },
    MuiFormHelperText: {
      root: {
        color: tokens.faint,
        marginLeft: 2,
        '&$error': { color: tokens.error },
      },
    },
    MuiSelect: {
      select: {
        '&.Mui-disabled': { cursor: 'not-allowed' },
        '&:focus': { backgroundColor: 'transparent' },
      },
      icon: { color: tokens.textDim },
    },

    MuiButton: {
      root: {
        borderRadius: RADIUS_SM,
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: '0.04em',
      },
      containedPrimary: {
        backgroundColor: tokens.text,
        color: tokens.onAccent,
        boxShadow: 'none',
        '&:hover': { backgroundColor: '#2C2E35', boxShadow: 'none' },
      },
      containedSecondary: {
        backgroundColor: tokens.card3,
        color: tokens.text,
        boxShadow: 'none',
        '&:hover': { backgroundColor: tokens.card2 },
      },
      outlined: { borderColor: tokens.lineStrong, color: tokens.text },
      outlinedPrimary: {
        borderColor: tokens.lineStrong,
        color: tokens.text,
        '&:hover': { borderColor: tokens.accent, backgroundColor: tokens.card2 },
      },
      text: { color: tokens.textDim, '&:hover': { backgroundColor: tokens.card2 } },
      textPrimary: { color: tokens.accent, '&:hover': { backgroundColor: tokens.card2 } },
      label: { letterSpacing: '0.04em' },
    },
    MuiIconButton: {
      root: { color: tokens.textDim, '&:hover': { backgroundColor: tokens.card2 } },
    },
    MuiFab: {
      primary: { backgroundColor: tokens.text, color: tokens.onAccent },
    },

    MuiChip: {
      root: {
        backgroundColor: tokens.card2,
        color: tokens.text,
        fontWeight: 600,
        fontSize: 12,
        height: 26,
      },
      outlined: { borderColor: tokens.lineStrong },
    },

    MuiPopover: { paper: { backgroundColor: tokens.card, border: `1px solid ${tokens.line}` } },
    MuiMenu: { paper: { backgroundColor: tokens.card, border: `1px solid ${tokens.line}` } },
    MuiMenuItem: {
      root: {
        color: tokens.text,
        fontSize: 14,
        '&:hover': { backgroundColor: tokens.card2 },
        '&.Mui-selected': { backgroundColor: tokens.card2 },
      },
    },
    MuiListItem: {
      root: { '&.Mui-selected': { backgroundColor: tokens.card2 } },
      button: { '&:hover': { backgroundColor: tokens.card2 } },
    },
    MuiListItemIcon: { root: { color: tokens.textDim, minWidth: 38 } },
    MuiListItemText: { primary: { color: tokens.text } },

    MuiTooltip: {
      tooltip: {
        backgroundColor: tokens.card3,
        color: tokens.text,
        border: `1px solid ${tokens.line}`,
        fontSize: 12,
        fontWeight: 500,
      },
      arrow: { color: tokens.card3 },
    },

    MuiSwitch: {
      switchBase: { '&.Mui-checked': { color: tokens.accent } },
      track: { backgroundColor: tokens.faint },
      colorPrimary: {
        '&.Mui-checked + $track': { backgroundColor: `${tokens.accentDim} !important` },
      },
    },
    MuiCheckbox: {
      root: { color: tokens.faint },
      colorPrimary: { '&.Mui-checked': { color: tokens.accent } },
      colorSecondary: { '&.Mui-checked': { color: tokens.accent } },
    },
    MuiRadio: {
      root: { color: tokens.faint },
      colorPrimary: { '&.Mui-checked': { color: tokens.accent } },
    },
    MuiLink: { root: { color: tokens.accent }, underlineHover: { color: tokens.accent } },

    MuiTablePagination: {
      root: { color: tokens.textDim },
      input: { margin: 0 },
    },
    MuiTabs: { indicator: { backgroundColor: tokens.accent } },
    MuiTab: { root: { textTransform: 'none', color: tokens.textDim } },

    MuiAvatar: {
      colorDefault: { color: tokens.text, backgroundColor: tokens.card3 },
    },
    MuiBackdrop: { root: { backgroundColor: 'rgba(0,0,0,0.6)' } },
    MuiSnackbarContent: {
      root: { backgroundColor: tokens.card3, color: tokens.text },
    },
    MuiCircularProgress: { colorPrimary: { color: tokens.accent } },

    RaSidebar: {
      drawerPaper: { backgroundColor: tokens.surface, width: 248 },
      fixed: { backgroundColor: tokens.surface },
    },
    RaMenuItemLink: {
      root: {
        color: tokens.textDim,
        borderRadius: RADIUS_SM,
        margin: '2px 10px',
        padding: '9px 12px',
        '&:hover': { backgroundColor: tokens.card2, color: tokens.text },
      },
      active: {
        color: tokens.text,
        backgroundColor: tokens.card2,
        fontWeight: 600,
      },
      icon: { color: 'inherit', minWidth: 36 },
    },
    RaDatagrid: {
      headerCell: {
        backgroundColor: 'transparent',
        color: tokens.faint,
        fontWeight: 600,
        fontSize: 11,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        borderBottom: `1px solid ${tokens.lineStrong}`,
      },
      row: { transition: 'background-color 120ms ease' },
      rowCell: { borderBottom: `1px solid ${tokens.line}` },
    },
    RaBulkActionsToolbar: { toolbar: { backgroundColor: tokens.card2 } },
    RaList: {
      content: {
        backgroundColor: tokens.card,
        border: `1px solid ${tokens.line}`,
        borderRadius: RADIUS,
        boxShadow: 'none',
        overflow: 'hidden',
      },
    },
    RaListToolbar: { toolbar: { paddingTop: 4 } },
    RaFilterForm: { form: { marginTop: 0 } },
    RaTopToolbar: { root: { paddingTop: 4, paddingBottom: 4 } },
    RaShow: {
      card: {
        backgroundColor: 'transparent',
        border: 'none',
        boxShadow: 'none',
      },
    },
    RaEdit: {
      card: {
        backgroundColor: tokens.card,
        border: `1px solid ${tokens.line}`,
        borderRadius: RADIUS,
        boxShadow: 'none',
      },
    },
    RaCreate: {
      card: {
        backgroundColor: tokens.card,
        border: `1px solid ${tokens.line}`,
        borderRadius: RADIUS,
        boxShadow: 'none',
      },
    },
    RaToolbar: {
      toolbar: {
        backgroundColor: tokens.card2,
        borderTop: `1px solid ${tokens.line}`,
      },
    },
    RaSaveButton: {
      button: { backgroundColor: tokens.text, color: tokens.onAccent },
    },
    RaLabeled: {
      label: {
        color: tokens.faint,
        fontSize: 11,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
      },
    },
    RaSimpleFormIterator: {
      form: { display: 'flex', justifyContent: 'space-between' },
      index: { display: 'none' },
    },
    RaImageInput: {
      dropZone: {
        backgroundColor: tokens.card2,
        border: `1px dashed ${tokens.lineStrong}`,
        color: tokens.textDim,
      },
    },
    RaPaginationActions: {
      currentPageButton: { color: tokens.accent, fontWeight: 700 },
    },
    RaAppBar: {
      toolbar: { backgroundColor: tokens.card },
      menuButton: { color: tokens.textDim },
    },
    RaLoadingIndicator: { loadedIcon: { color: tokens.textDim } },
    RaUserMenu: { userButton: { color: tokens.text } },
    RaNotification: {
      info: { backgroundColor: tokens.card3, color: tokens.text },
      success: { backgroundColor: tokens.card3, color: tokens.success },
      warning: { backgroundColor: tokens.card3, color: tokens.pending },
      error: { backgroundColor: tokens.card3, color: tokens.error },
    },
    RaAutocompleteSuggestionList: {
      suggestionsContainer: { zIndex: 9000 },
    },
  },
} as any);
